import { unref, type ComputedRef, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { QueryClient } from '@tanstack/vue-query';
import getAccountShipmentV2 from '@gpx-spa/queries/src/getAccountShipmentV2';
import getAccountShipmentRoutesBatch from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import type { ShipmentRouteBatchRow } from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { buildShipmentFormDeviceApiPayload } from '@/components/shipment/form/utils/shipment-form-payload';
import { applyRoutingStrategyToRouteSegments } from '@/components/shipment/form/utils/shipment-form-route-segments';
import { hasPersistableRoutePlan } from '@/components/shipment/form/utils/shipment-form-route-composer-snapshot';
import {
  normalizeRouteSegmentsForPersist,
  routeLegKey,
} from '@/components/shipment/form/utils/normalize-route-segments-for-persist';
import { computeStopsRouteFingerprint } from '@/utils/compute-stops-route-fingerprint';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import type { ShipmentStopIdRow } from '@/utils/device-api-shipment-payload';
import type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';
import { stopIdRowsFromFormStore } from '@/components/shipment/form/utils/persist/persistFormStops';
import type { RoutePersistIntent } from '@/components/shipment/form/utils/persist/shipment-route-persist-intent';
export {
  detectRouteSaveMode,
  captureRoutePersistIntent,
} from '@/components/shipment/form/utils/persist/shipment-route-persist-intent';

export type RouteSyncMode = 'upsert' | 'recreate';

export function haveFormRoutesChanged(
  selectedByPathId: Record<string, boolean> = {},
): boolean {
  const formStore = useShipmentFormStore();
  const { corridorBufferM, geometryQualityPercent, routePlan } =
    storeToRefs(formStore);
  if (!hasPersistableRoutePlan(routePlan.value)) return false;
  return formStore.haveRouteComposerChangedSincePersisted(
    corridorBufferM.value,
    geometryQualityPercent.value,
    selectedByPathId,
  );
}

function v2StopsToRouteIdRows(stopsApi: unknown): ShipmentStopIdRow[] {
  if (!Array.isArray(stopsApi)) return [];
  return [...stopsApi]
    .filter((s) => typeof (s as { id?: number }).id === 'number')
    .sort(
      (a, b) =>
        (a as { sequence: number }).sequence -
        (b as { sequence: number }).sequence,
    )
    .map((s) => ({
      id: (s as { id: number }).id,
      sequence: (s as { sequence: number }).sequence,
    }));
}

export async function resolveFormStopIdRowsForRoutes(
  queryClient: QueryClient,
  options: {
    accountId: Ref<number> | ComputedRef<number>;
    shipmentId: Ref<number | null> | ComputedRef<number | null>;
  },
  sid: number,
): Promise<ShipmentStopIdRow[]> {
  const fromStore = stopIdRowsFromFormStore();
  if (fromStore.length >= 2) return fromStore;

  const detail = await getAccountShipmentV2.ensureQueryData(
    queryClient,
    unref(options.accountId),
    sid,
  );
  return v2StopsToRouteIdRows(detail.stops);
}

type PersistRoutesDeps = {
  queryClient: QueryClient;
  postRouteSegment: (
    accountId: number,
    shipmentId: number,
    segment: unknown,
  ) => Promise<unknown>;
  patchRouteSegment: (
    accountId: number,
    shipmentId: number,
    routeId: number,
    segment: unknown,
  ) => Promise<unknown>;
  deleteRouteSegment: (
    accountId: number,
    shipmentId: number,
    routeId: number,
  ) => Promise<unknown>;
};

type PersistRoutesOptions = {
  accountId: Ref<number> | ComputedRef<number>;
  shipmentId: Ref<number | null> | ComputedRef<number | null>;
  stopsPort: Ref<ShipmentFormStopsPort | null>;
  routingStrategy?: Ref<RoutingStrategy> | ComputedRef<RoutingStrategy>;
  skipRoutePersistWhenServerRoutesExist?: Ref<boolean> | ComputedRef<boolean>;
  deviceIds?: Ref<readonly number[]> | ComputedRef<readonly number[]>;
};

async function buildDesiredRouteSegments(
  deps: Pick<PersistRoutesDeps, 'queryClient'>,
  options: PersistRoutesOptions,
  sid: number,
  persistOptions?: {
    deviceIdsOverride?: number[];
    intent?: RoutePersistIntent;
  },
): Promise<Record<string, unknown>[]> {
  const formStore = useShipmentFormStore();
  const {
    stops,
    routePlan,
    corridorBufferM,
    geometryQualityPercent,
    geoCache,
    generalName,
    generalCarrier,
  } = storeToRefs(formStore);

  const intent = persistOptions?.intent;
  const plan = intent?.routePlan ?? routePlan.value;
  if (!plan?.segments?.length) return [];
  const hasPaths = plan.segments.some(
    (s) => Array.isArray(s.paths) && s.paths.length > 0,
  );
  if (!hasPaths) return [];

  const idRows = await resolveFormStopIdRowsForRoutes(
    deps.queryClient,
    options,
    sid,
  );
  if (idRows.length < 2) return [];

  const routingStrategyValue =
    unref(options.routingStrategy) ?? 'originToDestination';

  const selectedByPathId =
    intent?.selectedByPathId ??
    unref(options.stopsPort.value?.selectedByPathId) ??
    {};

  const deviceIds =
    persistOptions?.deviceIdsOverride ?? [...(unref(options.deviceIds) ?? [])];

  const payloadResult = await buildShipmentFormDeviceApiPayload({
    shipmentId: sid,
    accountId: unref(options.accountId),
    deviceIds,
    name: generalName.value.trim(),
    carrier: generalCarrier.value?.trim() || null,
    stops: stops.value,
    routePlan: plan,
    geometryQualityPercent:
      intent?.geometryQualityPercent ?? geometryQualityPercent.value,
    corridorBufferM: intent?.corridorBufferM ?? corridorBufferM.value,
    selectedByPathId,
    geoCache: geoCache.value,
  });

  let segments = payloadResult
    .buildRouteSegments(idRows)
    .filter(
      (seg) => Array.isArray(seg.route_options) && seg.route_options.length > 0,
    );

  segments = applyRoutingStrategyToRouteSegments(
    segments,
    routingStrategyValue,
    idRows,
  );

  return normalizeRouteSegmentsForPersist(segments);
}

function rowsForShipment(
  rows: readonly ShipmentRouteBatchRow[],
  shipmentId: number,
): ShipmentRouteBatchRow[] {
  return rows.filter((row) => row.shipment_id === shipmentId);
}

async function resolveExistingRouteRows(
  queryClient: QueryClient,
  accountId: number,
  shipmentId: number,
  options: { preferFresh: boolean },
): Promise<ShipmentRouteBatchRow[]> {
  const rows = options.preferFresh
    ? await getAccountShipmentRoutesBatch.fetchQueryData(
        queryClient,
        accountId,
        [shipmentId],
      )
    : await getAccountShipmentRoutesBatch.ensureQueryData(
        queryClient,
        accountId,
        [shipmentId],
      );
  return rowsForShipment(rows, shipmentId);
}

function markRoutesPersisted(
  corridorBufferM: number,
  geometryQualityPercent: number,
  selectedByPathId: Record<string, boolean>,
  stops: ShipmentFormStopRow[],
): void {
  const formStore = useShipmentFormStore();
  formStore.markServerRoutesPersistedFromForm();
  formStore.markRouteComposerPersisted(
    corridorBufferM,
    geometryQualityPercent,
    selectedByPathId,
  );
  const fingerprint = computeStopsRouteFingerprint(stops);
  if (fingerprint != null) {
    formStore.serverHydratedRouteFingerprint = fingerprint;
  }
}

export async function syncFormRoutes(
  deps: PersistRoutesDeps,
  options: PersistRoutesOptions,
  syncOptions: {
    mode: RouteSyncMode;
    deviceIdsOverride?: number[];
    intent?: RoutePersistIntent;
    deferInvalidation?: boolean;
    /** When true, PUT stops already wiped all routes on device-api — skip DELETE in recreate. */
    serverRoutesAlreadyCleared?: boolean;
  },
): Promise<boolean> {
  const formStore = useShipmentFormStore();
  const {
    stops,
    routePlan,
    corridorBufferM,
    geometryQualityPercent,
    serverRoutesEmptyAtHydrate,
  } = storeToRefs(formStore);

  if (unref(options.skipRoutePersistWhenServerRoutesExist) === true) {
    if (!serverRoutesEmptyAtHydrate.value) return false;
  }

  const sid = unref(options.shipmentId);
  if (sid == null) return false;

  const intent = syncOptions.intent;
  const planForSync = intent?.routePlan ?? routePlan.value;
  if (!hasPersistableRoutePlan(planForSync)) return false;

  const desired = await buildDesiredRouteSegments(deps, options, sid, {
    deviceIdsOverride: syncOptions.deviceIdsOverride,
    intent,
  });
  if (!desired.length) return false;

  const accountId = unref(options.accountId);
  const selectedByPathId =
    intent?.selectedByPathId ??
    unref(options.stopsPort.value?.selectedByPathId) ??
    {};
  const corridorForMark = intent?.corridorBufferM ?? corridorBufferM.value;
  const geometryForMark =
    intent?.geometryQualityPercent ?? geometryQualityPercent.value;

  const existingRows =
    syncOptions.mode === 'recreate' &&
    syncOptions.serverRoutesAlreadyCleared === true
      ? []
      : await resolveExistingRouteRows(deps.queryClient, accountId, sid, {
          preferFresh: syncOptions.deferInvalidation === true,
        });

  let didMutate = false;

  if (syncOptions.mode === 'recreate') {
    if (syncOptions.serverRoutesAlreadyCleared !== true) {
      for (const row of existingRows) {
        await deps.deleteRouteSegment(accountId, sid, row.id);
        didMutate = true;
      }
    }
    for (const segment of desired) {
      await deps.postRouteSegment(accountId, sid, segment);
      didMutate = true;
    }
  } else {
    const desiredKeys = new Set(
      desired.map((seg) =>
        routeLegKey(seg.from_stop_id as number, seg.to_stop_id as number),
      ),
    );

    for (const segment of desired) {
      const fromId = segment.from_stop_id as number;
      const toId = segment.to_stop_id as number;
      const matches = existingRows.filter(
        (row) => row.from_stop_id === fromId && row.to_stop_id === toId,
      );

      if (!matches.length) {
        await deps.postRouteSegment(accountId, sid, segment);
        didMutate = true;
        continue;
      }

      const survivor = matches.reduce((best, row) =>
        row.id > best.id ? row : best,
      );
      for (const row of matches) {
        if (row.id === survivor.id) continue;
        await deps.deleteRouteSegment(accountId, sid, row.id);
        didMutate = true;
      }
      await deps.patchRouteSegment(accountId, sid, survivor.id, segment);
      didMutate = true;
    }

    for (const row of existingRows) {
      const key = routeLegKey(row.from_stop_id, row.to_stop_id);
      if (!desiredKeys.has(key)) {
        await deps.deleteRouteSegment(accountId, sid, row.id);
        didMutate = true;
      }
    }
  }

  if (didMutate) {
    markRoutesPersisted(
      corridorForMark,
      geometryForMark,
      selectedByPathId,
      stops.value,
    );
    if (!syncOptions.deferInvalidation) {
      await deps.queryClient.invalidateQueries({
        queryKey: getAccountShipmentRoutesBatch.queryKey(accountId, [sid]),
      });
    }
  }

  return didMutate;
}

/** @deprecated Use {@link syncFormRoutes} with explicit mode. */
export async function persistFormRoutes(
  deps: Omit<PersistRoutesDeps, 'patchRouteSegment' | 'deleteRouteSegment'> & {
    patchRouteSegment?: PersistRoutesDeps['patchRouteSegment'];
    deleteRouteSegment?: PersistRoutesDeps['deleteRouteSegment'];
  },
  options: PersistRoutesOptions,
  persistOptions?: {
    replaceAllAfterStopsSave?: boolean;
    deviceIdsOverride?: number[];
  },
): Promise<boolean> {
  const mode: RouteSyncMode = persistOptions?.replaceAllAfterStopsSave
    ? 'recreate'
    : 'upsert';
  return syncFormRoutes(
    {
      ...deps,
      patchRouteSegment:
        deps.patchRouteSegment ??
        (async () => {
          throw new Error('patchRouteSegment is required');
        }),
      deleteRouteSegment:
        deps.deleteRouteSegment ??
        (async () => {
          throw new Error('deleteRouteSegment is required');
        }),
    },
    options,
    {
      mode,
      deviceIdsOverride: persistOptions?.deviceIdsOverride,
    },
  );
}
